import { useEffect, useState } from 'react';
import { TriangleAlert, WifiOff } from 'lucide-react';
import { updateService, type UpdateResult } from '../../services/updateService';

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ClearSWCacheModal({ isOpen, onClose }: UpdateModalProps) {
  // The device's own network state, as in UpdateModal: the online status means
  // the API answers (#11623), and an API fault must not block clearing.
  const [hasNetwork, setHasNetwork] = useState(() => navigator.onLine);
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState<Exclude<
    UpdateResult,
    'reloading'
  > | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setIsClearing(false);
      setClearError(null);
      return;
    }
    // Re-read on open: a suspended PWA can miss the online/offline events.
    const updateHasNetwork = () => setHasNetwork(navigator.onLine);
    updateHasNetwork();
    window.addEventListener('online', updateHasNetwork);
    window.addEventListener('offline', updateHasNetwork);
    return () => {
      window.removeEventListener('online', updateHasNetwork);
      window.removeEventListener('offline', updateHasNetwork);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Clearing the cache reloads the app from its host, so offline it would leave
  // no app at all.
  const handleUpdate = async () => {
    setClearError(null);
    setIsClearing(true);
    const result = await updateService.updateToNewVersion();
    if (result !== 'reloading') {
      setClearError(result);
      setIsClearing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={isClearing ? undefined : onClose}
      />

      {/* Modal */}
      <div className="relative mx-4 max-w-md rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-4">
          <h2 className="mb-2 text-xl font-bold text-ink">Clear App Cache</h2>
          <p className="mb-4 text-inkMuted">
            Clear the app cache to apply the latest updates.
          </p>

          <div className="mb-4 border-l-4 border-amber-400 bg-amber-50 p-3">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-amber-700">
                  <strong>Important:</strong> Clearing cache may cause loss of
                  any in-progress inspection data that hasn't been saved.
                </p>
              </div>
            </div>
          </div>

          {!hasNetwork ? (
            <div className="mb-4 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
              <WifiOff className="mt-0.5 shrink-0 text-amber-600" size={18} />
              <p className="text-sm text-amber-700">
                <strong>You are offline.</strong> Clearing the cache needs a
                connection to download the app again - reconnect and try again.
              </p>
            </div>
          ) : clearError === 'unreachable' ? (
            <div className="mb-4 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
              <WifiOff className="mt-0.5 shrink-0 text-amber-600" size={18} />
              <p className="text-sm text-amber-700">
                <strong>The update server cannot be reached.</strong> Nothing
                was cleared - try again in a few minutes.
              </p>
            </div>
          ) : clearError === 'failed' ? (
            <div className="mb-4 flex items-start gap-2 border-l-4 border-amber-400 bg-amber-50 p-3">
              <TriangleAlert
                className="mt-0.5 shrink-0 text-amber-600"
                size={18}
              />
              <p className="text-sm text-amber-700">
                <strong>The cache could not be cleared.</strong> Try again in a
                few minutes.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isClearing}
            className="rounded-sm bg-line/40 px-4 py-2 text-inkMuted transition-colors hover:bg-line/60 active:bg-line/70 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={!hasNetwork || isClearing}
            className="rounded-sm bg-brand px-4 py-2 text-white transition-colors hover:bg-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isClearing ? 'Clearing...' : 'Clear Cache Now'}
          </button>
        </div>
      </div>
    </div>
  );
}
